import { useEffect, useRef } from "react"
import { cleanupClosedTabs } from "@/modules/Patient/config/tab.config"
import { useTabRef } from "@/routes/TabProvider"

/**
 * Custom hook to automatically refresh data when tracked tabs are closed
 *
 * It observes the shared `tabRefs` registry and compares the tab count
 * from when this window/tab lost focus to when it regains focus again.
 * If tabs were closed in between, it invokes the provided callback.
 *
 * @param refreshCallback - Function to call when tabs are closed and user returns to this tab
 * @param enabled - Whether the refresh mechanism is enabled (default: true)
 *
 * @example
 * // In a component that needs to refresh when tabs close
 * useTabRefresh(() => {
 *   dispatch(getCaseByPatient({ patientId, isCaseArchived: true }))
 * })
 */
export const useTabRefresh = (
  refreshCallback: () => void,
  enabled: boolean = true
) => {
  const tabRefs = useTabRef()
  const previousTabCountRef = useRef<number>(0)
  // Use ref to store the latest callback to avoid re-setting up event listeners
  const callbackRef = useRef(refreshCallback)

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = refreshCallback
  }, [refreshCallback])

  // Track initial tab count
  useEffect(() => {
    if (tabRefs?.current && enabled) {
      previousTabCountRef.current = Object.keys(tabRefs.current).length
    }
  }, [tabRefs, enabled])

  // Refresh when parent window regains focus/visibility and tabs were closed
  useEffect(() => {
    if (!enabled) return

    const getTabCount = () => {
      if (!tabRefs?.current) return 0
      return Object.keys(tabRefs.current).length
    }

    const checkAndRefresh = () => {
      if (!tabRefs?.current) return

      // Clean up closed tabs
      cleanupClosedTabs(tabRefs)

      // Get current tab count after cleanup
      const currentTabCount = getTabCount()

      // Read previous tab count that was captured when tab lost focus/visibility
      const previousTabCount = previousTabCountRef.current

      // Update ref for next check
      previousTabCountRef.current = currentTabCount

      // If tabs were closed, trigger refresh callback using the latest callback
      if (previousTabCount > currentTabCount) {
        callbackRef.current()
      }
    }

    // Capture tab count when this window loses focus
    const handleBlur = () => {
      previousTabCountRef.current = getTabCount()
    }

    // Handle window focus (when user switches back to this tab)
    const handleFocus = () => {
      // Small delay to ensure tab cleanup has happened
      setTimeout(checkAndRefresh, 100)
    }

    // Handle visibility change (when tab becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // When going to background, remember current tab count
        previousTabCountRef.current = getTabCount()
      } else if (document.visibilityState === "visible") {
        setTimeout(checkAndRefresh, 100)
      }
    }

    window.addEventListener("blur", handleBlur)
    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.removeEventListener("blur", handleBlur)
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [enabled, tabRefs]) // Removed refreshCallback from deps - using ref instead
}